import { messages } from "./messageCatalogue";
import { MappedSheet, SkippedRow, SkippedRowReason } from "./validationTypes";

export const WHY_LEFT_OUT_COLUMN = "Why it was left out";
export const SHEET_COLUMN = "Sheet";

// The rows left out of a worksheet, shaped like the worksheet, with each kept row's repeats grouped.
// Rows from several sheets are grouped by sheet, in the order their rows arrive, and say which sheet they are on.
export function buildSkippedRowsSheet(worksheet: string, rows: SkippedRow[]): MappedSheet | undefined {
  if (rows.length === 0) return undefined;
  const getSheetOrder = buildSheetOrderLookup(rows);
  const skippedRows = [...rows].sort(
    (first, second) => getSheetOrder(first) - getSheetOrder(second) || getGroupOrder(first) - getGroupOrder(second)
  );

  const columns: Record<string, string[]> = { [WHY_LEFT_OUT_COLUMN]: skippedRows.map(messages.leftOutReason) };
  if (skippedRows.some((row) => row.sheetName)) {
    columns[SHEET_COLUMN] = skippedRows.map((row) => row.sheetName ?? "");
  }
  const fields = [...new Set(skippedRows.flatMap((row) => Object.keys(row.values)))];
  for (const field of fields) {
    columns[field] = skippedRows.map((row) => row.values[field] ?? "");
  }

  return { worksheet, rowNumbers: skippedRows.map((row) => row.rowNumber), columns };
}

// Rows without a sheet name come first; sheets follow in the order their first row appears
function buildSheetOrderLookup(rows: SkippedRow[]): (row: SkippedRow) => number {
  const orderBySheet = new Map<string, number>();
  for (const { sheetName } of rows) {
    if (sheetName && !orderBySheet.has(sheetName)) orderBySheet.set(sheetName, orderBySheet.size + 1);
  }
  return (row) => (row.sheetName ? orderBySheet.get(row.sheetName) ?? 0 : 0);
}

// Rows with nothing to match on come first, then each group of repeats under the row it repeats
function getGroupOrder(row: SkippedRow) {
  switch (row.reason) {
    case SkippedRowReason.Keyless:
    case SkippedRowReason.BlankJoinKey:
      return -1;
    case SkippedRowReason.Duplicate:
    case SkippedRowReason.RepeatedJoinKey:
      return row.duplicateOfRowNumber;
    case SkippedRowReason.NoMatchingRow:
      return row.rowNumber;
  }
}
